import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Music, Palette, Loader2, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import CoverFlow from "@/components/CoverFlow";
import { listProjects, createProject, duplicateProject, deleteProject, updateProject, getLast, setLast } from "@/lib/projects";
import { defaultSong } from "@/studio/songData";

const CFG = {
  studio: { title: "Your Songs", accent: "#fbbf24", icon: Music, kindLabel: "song", newLabel: "New Song", route: "/w/studio", eyebrow: "The Studio" },
  art: { title: "Your Artwork", accent: "#38bdf8", icon: Palette, kindLabel: "artwork", newLabel: "New Artwork", route: "/w/art", eyebrow: "The Art Room" },
};

export function SongThumb({ meta }) {
  const tracks = meta?.tracks || [], bars = meta?.bars || 32;
  return (
    <div className="h-full w-full bg-[#1b1c20] p-4 flex flex-col">
      <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500"><span>{meta?.bpm || "—"} BPM</span><span>{tracks.length} tracks · {bars} bars</span></div>
      <svg viewBox={`0 0 ${bars} ${Math.max(tracks.length, 5) * 6}`} preserveAspectRatio="none" className="flex-1 mt-3 w-full">
        {tracks.map((t, i) => (
          <g key={i}><rect x="0" y={i * 6} width={bars} height="5" fill="rgba(255,255,255,.04)" />{(t.clips || []).map(([s, l], j) => <rect key={j} x={s} y={i * 6 + 0.4} width={l} height="4.2" rx="0.4" fill={t.color || "#fbbf24"} opacity=".9" />)}</g>
        ))}
        {!tracks.length && <text x={bars / 2} y="15" textAnchor="middle" fill="#52525b" fontSize="4">Empty song</text>}
      </svg>
    </div>
  );
}

export function ArtThumb({ meta }) {
  return meta?.thumb ? (
    <div className="h-full w-full checker flex items-center justify-center"><img src={meta.thumb} alt="" className="max-h-full max-w-full object-contain" draggable={false} /></div>
  ) : (
    <div className="h-full w-full bg-[#1e1f22] flex flex-col items-center justify-center text-zinc-500 gap-2"><Layers className="w-8 h-8" /><span className="text-xs">{meta?.w ? `${meta.w}×${meta.h}` : "Blank canvas"}</span></div>
  );
}

export default function Library({ kind }) {
  const cfg = CFG[kind];
  const nav = useNavigate();
  const loc = useLocation();
  const [items, setItems] = useState(null);
  const [index, setIndex] = useState(0);
  const [rename, setRename] = useState(null);
  const [del, setDel] = useState(null);
  const browse = loc.pathname.endsWith("/library");

  useEffect(() => {
    listProjects(kind).then((ps) => {
      const last = getLast(kind);
      if (!browse && last && ps.some((p) => p.id === last)) return nav(`${cfg.route}/${last}`, { replace: true });
      if (!browse && !last && ps.length) return nav(`${cfg.route}/${ps[0].id}`, { replace: true });
      setItems(ps); setIndex(Math.max(0, ps.findIndex((p) => p.id === last)));
    }).catch(() => { toast.error("Could not load your library"); setItems([]); });
  }, [kind]); // eslint-disable-line

  const open = (p) => { setLast(kind, p.id); nav(`${cfg.route}/${p.id}`); };
  const onNew = async () => {
    const n = items.length + 1, first = items.length === 0;
    const p = kind === "studio"
      ? await createProject("studio", first ? "Midnight Pocket" : `Untitled Song ${n}`, first ? defaultSong() : { ...defaultSong(), title: `Untitled Song ${n}`, tracks: [] }, { bpm: 92, bars: 32, tracks: [] })
      : await createProject("art", first ? "Gig Flyer" : `Untitled Artwork ${n}`, null, first ? { demo: true } : {});
    open(p);
  };
  const onDuplicate = async (p) => { const d = await duplicateProject(p.id); setItems((a) => [d, ...a]); setIndex(0); toast.success(`Duplicated “${p.name}”`); };
  const onDelete = async () => { const p = del; setDel(null); await deleteProject(p.id); setItems((a) => a.filter((x) => x.id !== p.id)); setIndex((i) => Math.max(0, Math.min(i, items.length - 2))); if (getLast(kind) === p.id) setLast(kind, null); toast.success("Deleted"); };
  const saveRename = async () => { const { p, name } = rename; setRename(null); if (!name.trim() || name === p.name) return; await updateProject(p.id, { name: name.trim() }); setItems((a) => a.map((x) => (x.id === p.id ? { ...x, name: name.trim() } : x))); };

  const I = cfg.icon;
  return (
    <div className="h-full flex flex-col bg-[#0b0b0c] text-white grain relative overflow-hidden" data-testid={`library-${kind}`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 30%, ${cfg.accent}18, transparent 60%)` }} />
      <div className="relative px-10 pt-10 flex items-end justify-between">
        <div className="fade-up">
          <div className="text-[11px] tracking-[.3em] uppercase" style={{ color: cfg.accent }}>{cfg.eyebrow}</div>
          <h1 className="text-4xl sm:text-5xl font-light mt-2 flex items-center gap-4"><I className="w-9 h-9" style={{ color: cfg.accent }} />{cfg.title}</h1>
          <p className="text-zinc-500 mt-2 text-sm">Swipe, scroll or use ← → to browse. Click the front card to slide it open.</p>
        </div>
        {items && <div className="text-sm text-zinc-500 font-mono" data-testid="library-count">{items.length} {cfg.kindLabel}{items.length === 1 ? "" : "s"}</div>}
      </div>
      <div className="relative flex-1 min-h-0">
        {!items ? <div className="h-full flex items-center justify-center text-zinc-500 gap-2"><Loader2 className="w-5 h-5 animate-spin" />Loading your {cfg.kindLabel}s…</div> : (
          <CoverFlow items={items} index={index} setIndex={setIndex} accent={cfg.accent} kindLabel={cfg.kindLabel} newLabel={cfg.newLabel}
            renderCard={(p) => (kind === "studio" ? <SongThumb meta={p.meta} /> : <ArtThumb meta={p.meta} />)}
            onOpen={open} onNew={onNew} onRename={(p) => setRename({ p, name: p.name })} onDuplicate={onDuplicate} onDelete={(p) => setDel(p)} />
        )}
      </div>
      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent className="max-w-sm bg-[#111215] border-white/10">
          <DialogHeader><DialogTitle className="font-light">Rename {cfg.kindLabel}</DialogTitle><DialogDescription>Give it a name you'll recognise in the library.</DialogDescription></DialogHeader>
          {rename && <Input autoFocus value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveRename()} data-testid="rename-input" className="bg-[#16181b] border-white/5" />}
          <DialogFooter><button onClick={saveRename} data-testid="rename-save" className="h-9 px-4 rounded-lg text-sm font-medium text-zinc-900" style={{ background: cfg.accent }}>Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-w-sm bg-[#111215] border-white/10">
          <DialogHeader><DialogTitle className="font-light">Delete “{del?.name}”?</DialogTitle><DialogDescription>This permanently removes the {cfg.kindLabel} from your account.</DialogDescription></DialogHeader>
          <DialogFooter><button onClick={() => setDel(null)} className="h-9 px-4 rounded-lg text-sm border border-white/10">Cancel</button><button onClick={onDelete} data-testid="delete-confirm" className="h-9 px-4 rounded-lg text-sm bg-red-500 hover:bg-red-400 text-white">Delete</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
